'use client';

/**
 * Shared status banner.
 *
 * Settings, Data Rights, Upload Job, Forgot Password and Reset Password each
 * hand-rolled this markup, with their own padding, icon and colour choices —
 * and several of them announced nothing at all to a screen reader. This owns
 * the vocabulary once: tinted ground, hairline in the status hue, the status
 * icon, and the right live-region semantics for the variant.
 *
 * Same argument AGENTS.md already makes for Button and the field primitives:
 * never hand-roll a coloured status <div>.
 */

import { useId, type ReactNode } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  X,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';

export type AlertVariant = 'success' | 'danger' | 'warning' | 'info';

interface VariantStyle {
  container: string;
  accent: string;
  icon: LucideIcon;
}

/* Tint ground + 1px hairline in the status hue; the icon and title carry the
   solid tone, the body stays in the paper's own ink. Both themes resolve
   through the token pairs, so nothing here needs a `dark:` variant. */
const VARIANT_STYLES: Record<AlertVariant, VariantStyle> = {
  success: {
    container: 'bg-success-light border-success',
    accent: 'text-success',
    icon: CheckCircle2,
  },
  danger: {
    container: 'bg-danger-light border-danger',
    accent: 'text-danger',
    icon: XCircle,
  },
  warning: {
    container: 'bg-warning-light border-warning',
    accent: 'text-warning',
    icon: AlertTriangle,
  },
  info: {
    container: 'bg-info-light border-info',
    accent: 'text-info',
    icon: Info,
  },
};

export interface AlertProps {
  variant: AlertVariant;
  /** Bold lead line above the body, in the status tone. */
  title?: string;
  children?: ReactNode;
  /** Renders the dismiss control; requires `dismissLabel`. */
  onDismiss?: () => void;
  /** Accessible name for the dismiss control (localized by the caller). */
  dismissLabel?: string;
  className?: string;
}

export function Alert({
  variant,
  title,
  children,
  onDismiss,
  dismissLabel,
  className,
}: AlertProps) {
  const { container, accent, icon: Icon } = VARIANT_STYLES[variant];
  const titleId = useId();

  // An error interrupts; everything else waits for a pause. `alert` and
  // `status` carry assertive/polite implicitly, so no aria-live is needed.
  const isError = variant === 'danger';

  return (
    <div
      role={isError ? 'alert' : 'status'}
      aria-labelledby={title ? titleId : undefined}
      className={cn(
        'flex items-start gap-3 rounded-lg border p-4',
        container,
        className
      )}
    >
      <Icon
        className={cn('mt-0.5 h-5 w-5 flex-shrink-0', accent)}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1 text-sm text-primary">
        {title && (
          <p id={titleId} className={cn('font-semibold mb-0', accent)}>
            {title}
          </p>
        )}
        {children && <div className={cn(title && 'mt-1')}>{children}</div>}
      </div>
      {onDismiss && dismissLabel && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onDismiss}
          aria-label={dismissLabel}
          className={cn('-mr-1 -mt-1 h-7 w-7 flex-shrink-0', accent)}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </Button>
      )}
    </div>
  );
}

export default Alert;
